import type { UserEventDashboard, AppNotification } from '@fellowship/shared'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { EYEBROW_STYLE } from '../format'
import { UpNextCard } from '../UpNextCard'
import { HostedEventRow } from '../HostedEventRow'
import { FollowedChurchCard } from '../FollowedChurchCard'
import { StatTiles } from '../StatTiles'
import { NotificationRow } from '../NotificationRow'
import { useMarkNotificationRead } from '../../../hooks/useNotifications'

interface OverviewTabProps {
  dashboard: UserEventDashboard
  notifications: AppNotification[]
}

function Eyebrow({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ ...EYEBROW_STYLE, marginBottom: 12, ...style }}>{children}</div>
}

export function OverviewTab({ dashboard, notifications }: OverviewTabProps) {
  const isMobile = useIsMobile()
  const markRead = useMarkNotificationRead()
  const { upcoming, hostedEvents, followedChurches, past } = dashboard
  const upcomingHosted = hostedEvents.filter((e) => e.nextOccurrenceStartsAt !== null)
  const recent = notifications.slice(0, 2)

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) 300px',
        gap: isMobile ? 28 : 36,
        alignItems: 'start',
      }}
    >
      {/* Main column */}
      <div>
        <Eyebrow>Up next</Eyebrow>
        <UpNextCard entry={upcoming[0] ?? null} />

        {upcomingHosted.length > 0 && (
          <>
            <Eyebrow style={{ marginTop: 30 }}>You&rsquo;re hosting · {upcomingHosted.length}</Eyebrow>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {upcomingHosted.map((summary) => (
                <HostedEventRow key={summary.id} summary={summary} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Rail */}
      <aside>
        <StatTiles
          rsvps={upcoming.length + past.length}
          hosted={hostedEvents.length}
          following={followedChurches.length}
        />

        {followedChurches.length > 0 && (
          <>
            <Eyebrow style={{ marginTop: 24 }}>Following · {followedChurches.length}</Eyebrow>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {followedChurches.map((church) => (
                <FollowedChurchCard key={church.id} church={church} compact />
              ))}
            </div>
          </>
        )}

        {recent.length > 0 && (
          <>
            <Eyebrow style={{ marginTop: 24 }}>Recent</Eyebrow>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {recent.map((n) => (
                <NotificationRow key={n.id} n={n} compact onRead={(id) => markRead.mutate(id)} />
              ))}
            </div>
          </>
        )}
      </aside>
    </div>
  )
}
